/**
 * The Errors chart (19/10/2026): thin columns — one per Danish hour today,
 * one per run day over a range — stacked by tone (critical in red at the
 * bottom, errors in amber, read failures in grey) with 2 px gaps, hairline
 * gridlines, a legend, and a hover card per column naming the kinds.
 * Folded by default under one row (26/10/2026, client: "Collapse this on
 * errors page") that sums it up — how many errors, the busiest column.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo, useState } from 'react';
import { BarChart3 } from 'lucide-react';
import type { DropGenErrorRow, DropGenErrorTone, DropGenErrorsReport } from '@trafficpulse/shared';

import { Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';

import { niceTicks } from '../../timeline/day/day-hours';
import { Collapse } from '../../timeline/section/Collapse';
import { SectionToggle } from '../../timeline/section/SectionToggle';
import { kindWords, TONE_COLOR, TONE_LABEL } from '../errors-words';
import { type ErrorBucket, errorBuckets, toneLine } from './error-buckets';

const TONES: DropGenErrorTone[] = ['critical', 'error', 'warning'];
const PLOT = 'relative h-[120px]';

/** "9 errors · busiest 21/10 (6)" — the folded row's one line. */
export function chartSummary(buckets: ErrorBucket[]): string {
  const total = buckets.reduce((n, b) => n + b.total, 0);
  if (total === 0) return 'no error';
  const top = buckets.reduce((a, b) => (b.total > a.total ? b : a));
  return `${total} error${total === 1 ? '' : 's'} · busiest ${top.label} (${top.total})`;
}

const Tip: FC<{ b: ErrorBucket }> = ({ b }) => (
  <div className="flex flex-col gap-0.5 text-xs">
    <span className="font-semibold">
      {b.title} — {b.total} error{b.total === 1 ? '' : 's'}
    </span>
    <span className="text-muted-foreground">{toneLine(b)}</span>
    {Object.entries(b.byKind)
      .sort((a, c) => c[1] - a[1])
      .slice(0, 4)
      .map(([kind, n]) => (
        <span key={kind} className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: TONE_COLOR[kindWords(kind).tone] }} aria-hidden />
          {kindWords(kind).label} · {n}
        </span>
      ))}
  </div>
);

const Column: FC<{ b: ErrorBucket; max: number }> = ({ b, max }) => (
  <Tooltip className="flex h-full w-full" content={b.total > 0 ? <Tip b={b} /> : null}>
    <span tabIndex={b.total > 0 ? 0 : -1} aria-label={`${b.title}: ${b.total} errors`} className="group flex h-full w-full items-end justify-center outline-none transition-colors hover:bg-primary/[0.06] focus-visible:bg-primary/10" data-testid="error-column">
      {b.total > 0 ? (
        <span className="flex w-[min(22px,62%)] flex-col-reverse gap-[2px] overflow-hidden rounded-t-[4px] transition-[height] duration-500 ease-out motion-reduce:transition-none" style={{ height: `${(b.total / max) * 100}%` }}>
          {TONES.map((t) => (b.byTone[t] > 0 ? <span key={t} className="block min-h-[2px]" style={{ flexGrow: b.byTone[t], flexBasis: 0, backgroundColor: TONE_COLOR[t] }} /> : null))}
        </span>
      ) : null}
    </span>
  </Tooltip>
);

export const ErrorsChart: FC<{ report: DropGenErrorsReport; rows: DropGenErrorRow[]; days: number; tz: string | null }> = ({ report, rows, days, tz }) => {
  const buckets = useMemo(() => errorBuckets(report, rows, days, tz), [report, rows, days, tz]);
  const ticks = niceTicks(Math.max(...buckets.map((b) => b.total)));
  const max = ticks[ticks.length - 1];
  const at = (v: number) => `${(v / max) * 100}%`;
  const every = days === 1 ? 3 : buckets.length > 14 ? 5 : 1;
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-border/40" data-testid="errors-chart">
      <SectionToggle icon={BarChart3} title={days === 1 ? 'Today by the hour · Danish time' : `By day · ${days} run days`} summary={chartSummary(buckets)} open={open} onToggle={() => setOpen(!open)} />
      <Collapse open={open}>
        <div className="flex flex-col gap-2 px-5 pb-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            {TONES.map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 text-muted-foreground">
                <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: TONE_COLOR[t] }} aria-hidden />
                {TONE_LABEL[t]}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-[40px_1fr] gap-x-2">
            <div className={PLOT} aria-hidden>
              {ticks.map((t) => (
                <span key={t} className="absolute right-0 translate-y-1/2 font-mono text-[10px] tabular-nums text-muted-foreground" style={{ bottom: at(t) }}>
                  {t}
                </span>
              ))}
            </div>
            <div className={cn(PLOT, 'border-b border-border')}>
              {ticks.slice(1).map((t) => (
                <span key={t} className="pointer-events-none absolute inset-x-0 border-t border-border/50" style={{ bottom: at(t) }} aria-hidden />
              ))}
              <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))` }}>
                {buckets.map((b) => (
                  <Column key={b.key} b={b} max={max} />
                ))}
              </div>
            </div>
            <span />
            <div className="grid font-mono text-[10px] tabular-nums text-muted-foreground" style={{ gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))` }} aria-hidden>
              {buckets.map((b, i) => (
                <span key={b.key} className="truncate text-center">
                  {i % every === 0 ? b.label : ''}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};
